import User from '@models/user/User';
import { PlayerDetailsFieldType } from '@models/user/enums';
import {
    BgcLicence,
    LicenceState,
} from '@accountFederation/modules/licencePreferences/types';
import { AuthenticationError } from '@superbet-group/web.lib.utilities';
import {
    ActionContext,
    Dispatch,
} from 'vuex';
import { IAuthenticationState } from '@store/modules/data/authentication/types';
import { IState } from '@app/store';
import { isComingFromLangRedirect } from '@modules/authentication/isComingFromLangRedirect';
import { isMultiLoginTarget } from '@app/multiLogin/isMultiLoginTarget';
import {
    GeoLocationPingResult,
    GeoLocationPingStatus,
} from '@core/services/location/types';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';

export function getMissingUserFields(user: User): PlayerDetailsFieldType[] {
    const fields: PlayerDetailsFieldType[] = [];

    if (!user.isVerifiedIdCard) {
        fields.push(PlayerDetailsFieldType.kycChecked);
        fields.push(PlayerDetailsFieldType.firstDepositDate);
    }
    if (user.shouldVerifySms) {
        fields.push(PlayerDetailsFieldType.shouldVerifySms);
    }
    if (!user.hasMadeDeposit) {
        fields.push(PlayerDetailsFieldType.hasMadeDeposit);
    }
    if (user.nationality && !user.phone) {
        fields.push(PlayerDetailsFieldType.phone);
    }
    if (user.reKycStatus) {
        fields.push(PlayerDetailsFieldType.reKycStatus);
    }

    return fields;
}

export function pickUserByFields(user: Partial<User>, fields?: PlayerDetailsFieldType[]): Partial<User> {
    if (!fields || fields.length === 0) {
        return user;
    }

    const strippedUser: Partial<User> = {};
    const fieldsSet = new Set(fields);

    if (fieldsSet.has(PlayerDetailsFieldType.kycChecked)) {
        strippedUser.isVerifiedIdCard = user.isVerifiedIdCard;
        strippedUser.isTempAccount = !user.isVerifiedIdCard;
        strippedUser.firstDepositDate = user.firstDepositDate;
    }

    if (fieldsSet.has(PlayerDetailsFieldType.shouldVerifySms)) {
        strippedUser.shouldVerifySms = user.shouldVerifySms;
    }

    if (fieldsSet.has(PlayerDetailsFieldType.hasMadeDeposit)) {
        strippedUser.hasMadeDeposit = user.hasMadeDeposit;
    }

    if (fieldsSet.has(PlayerDetailsFieldType.phone)) {
        strippedUser.phone = user.phone;
    }

    if (user.reKycStatus) {
        strippedUser.reKycStatus = user.reKycStatus;
    }

    return strippedUser;
}

export function hasAtLeastOneLicenceEnabled(licences: Partial<Record<BgcLicence, LicenceState>>): boolean {
    if (!licences) {
        return false;
    }

    const licencesList = Object.values(licences);
    return licencesList.some((licence) => licence.accepted);
}

export async function initializeSessionHelper(
    {
        cachedUser,
        context,
        clearSession,
        fetchUser,
        userData,
    }: {
        cachedUser: Nullable<User>;
        context: ActionContext<IAuthenticationState, IState>;
        clearSession: (context: ActionContext<IAuthenticationState, IState>) => void;
        fetchUser: () => Promise<Nullable<User>>;
        userData: (user: User) => Promise<User>;
    }) {
    const user = await fetchUser();

    if (!user) {
        context.dispatch('ui/user/preferences/initializeUserPreferences', {}, { root: true });
        context.dispatch(
            'setIsCheckedAuthentication',
            true,
        );
        clearSession(context);
        if (cachedUser) {
            context.dispatch(
                'data/authentication/sessionExpired',
                null,
                { root: true },
            );
        }

        return;
    }

    context.dispatch(
        'postLogin',
        {
            user: await userData(user),
            isLogin: false,
        },
    );

    context.dispatch(
        'ui/user/bonuses/checkActiveBonuses',
        null,
        { root: true },
    );
}

/**
 * This function is here because iCore is fragile, and we can't call getPlayerDetails on every refresh,
 * and we have this subset fields logic for iCore.
 */
export async function fetchUser(
    {
        cachedUser,
        fetchUserDetails,
    }: FetchUserParams): Promise<Nullable<User>> {
    try {
        if (isComingFromLangRedirect() || isMultiLoginTarget) {
            return await fetchUserDetails() as Promise<User>;
        }

        if (!cachedUser) {
            return null;
        }

        const fields = getMissingUserFields(cachedUser);
        const serverUser = await fetchUserDetails(fields);
        return {
            ...cachedUser,
            ...serverUser,
        };
    } catch (e) {
        if (e instanceof AuthenticationError) {
            return null;
        }
        throw e;
    }
}

export function handleGeoLocationPingResult(geoLocationResult: GeoLocationPingResult, dispatch: Dispatch, closeModals = false): boolean {
    if (geoLocationResult.status === GeoLocationPingStatus.PermissionPrompt) {
        if (closeModals) {
            dispatch('ui/modal/closeModal', null, { root: true });
        }
        dispatch(
            // eslint-disable-next-line sonarjs/no-duplicate-string
            'ui/modal/setModal',
            {
                code: modalCodes.geoLocationCheck,
                data: {
                    infoModal: true,
                },
            },
            { root: true },
        );
        return true;
    } else if (geoLocationResult.status !== GeoLocationPingStatus.Ok) {
        if (closeModals) {
            dispatch('ui/modal/closeModal', null, { root: true });
        }
        dispatch(
            'ui/modal/setModal',
            {
                code: modalCodes.geoLocationCheck,
                data: {
                    code: geoLocationResult.code,
                    clientId: geoLocationResult.clientId,
                },
            },
            { root: true },
        );
        return true;
    }
    return false;
}

type FetchUserParams = {
    cachedUser: Nullable<User>;
    fetchUserDetails: (fields?: PlayerDetailsFieldType[]) => Promise<Partial<User>>;
};
